/* Build Your Pair — live pillow customizer + cart carry-through.
   Ports scripts/compose_two_dog.py + breed_sizes.py to the browser: draws the blank
   pillow, then the two chosen dog cutouts laid out exactly like the print compositor,
   and redraws instantly as the dropdowns change. Data (cutout URLs, per-breed metrics,
   pillow-front quad, gamma) is injected by the section as window.SOF2.

   CARRY-THROUGH (added): on Add-to-cart we render the current canvas to a JPEG,
   upload it to the twodog-upload Worker, and stash the returned URL in a hidden
   line-item property `properties[_preview_image]` so the customer's exact pillow
   shows in the cart drawer and in the order confirmation email. The upload is
   best-effort and time-boxed — if it errors or runs past UPLOAD_TIMEOUT_MS we
   submit the form anyway so checkout is never blocked.

   >>> This is the drop-in replacement for assets/sof-two-dog.js. <<<
   The only change vs the live asset is the block marked "CARRY-THROUGH" at the
   bottom (the constant near the top + the DOMContentLoaded submit handler).  */
(function () {
  "use strict";
  var D = window.SOF2;
  if (!D) return;

  // ---- carry-through config ----
  var UPLOAD_ENDPOINT = "https://twodog-upload.atari-integrations.workers.dev/upload";
  var UPLOAD_TIMEOUT_MS = 4000; // never block checkout longer than this

  // --- print-space layout constants (match compose_two_dog.compose @ canvas=3000) ---
  var C = 3000, TALL = 0.52, BASELINE = 0.9, GAP = 70, MARGIN = 0.05;
  var imgCache = {};

  function loadImg(url) {
    return new Promise(function (res, rej) {
      if (imgCache[url]) return res(imgCache[url]);
      var im = new Image();
      im.crossOrigin = "anonymous";           // keep canvas untainted so we can export it
      im.onload = function () { imgCache[url] = im; res(im); };
      im.onerror = function () { rej(new Error("img " + url)); };
      im.src = url;
    });
  }

  function relScales(lb, rb) {
    var wl = D.metrics[lb].withers, wr = D.metrics[rb].withers, m = Math.max(wl, wr);
    return [Math.pow(wl / m, D.gamma), Math.pow(wr / m, D.gamma)];
  }

  // returns dog boxes in C-space (the full print square)
  function layout(lb, rb) {
    var mL = D.metrics[lb].left, mR = D.metrics[rb].right;
    var s = relScales(lb, rb), sL = s[0], sR = s[1];
    var margin = C * MARGIN, baseY = C * BASELINE, availW = C - 2 * margin, availH = baseY - margin;
    var T = TALL * C, L, R, lgx, rgx, bboxW, maxH;
    for (var i = 0; i < 8; i++) {
      var scL = T * sL / mL.body_h, scR = T * sR / mR.body_h;
      L = { w: mL.w * scL, h: mL.h * scL, glass: mL.glass_x * scL };
      R = { w: mR.w * scR, h: mR.h * scR, glass: mR.glass_x * scR };
      lgx = L.glass; rgx = R.glass;
      bboxW = lgx + GAP - rgx + R.w; maxH = Math.max(L.h, R.h);
      if (bboxW <= availW && maxH <= availH) break;
      T *= Math.min(availW / bboxW, availH / maxH) * 0.99;
    }
    var x0 = (C - bboxW) / 2, lX = x0, rX = x0 + lgx + GAP - rgx;
    var lY = baseY - L.h, rY = baseY - R.h;
    var pairTop = Math.min(lY, rY), vshift = (C - (baseY - pairTop)) / 2 - pairTop;
    lY += vshift; rY += vshift;
    return { L: L, R: R, lX: lX, lY: lY, rX: rX, rY: rY };
  }

  // draw pillow + pair to a canvas at `size` px. Returns the canvas.
  function render(cv, base, lImg, rImg, lb, rb, size) {
    cv.width = size; cv.height = size;
    var ctx = cv.getContext("2d");
    ctx.clearRect(0, 0, size, size);
    ctx.drawImage(base, 0, 0, size, size);
    // pillow-front box (fraction quad -> pixels); treat as axis box (front is ~square)
    var q = D.quad, fx0 = q[0][0] * size, fy0 = q[0][1] * size;
    var fw = (q[1][0] - q[0][0]) * size, fh = (q[3][1] - q[0][1]) * size;
    var k = fw / C, kh = fh / C;   // C-space -> front-box px
    var lo = layout(lb, rb);
    ctx.drawImage(lImg, fx0 + lo.lX * k, fy0 + lo.lY * kh, lo.L.w * k, lo.L.h * kh);
    ctx.drawImage(rImg, fx0 + lo.rX * k, fy0 + lo.rY * kh, lo.R.w * k, lo.R.h * kh);
    return cv;
  }

  var previewCanvas, baseImg, current = { left: null, right: null };

  function redraw() {
    var lb = current.left, rb = current.right;
    loadImg(D.base).then(function (b) {
      baseImg = b;
      if (!lb || !rb) {                       // blank pillow until both chosen
        previewCanvas.width = 1000; previewCanvas.height = 1000;
        previewCanvas.getContext("2d").drawImage(b, 0, 0, 1000, 1000);
        previewCanvas.dataset.ready = ""; return;
      }
      Promise.all([loadImg(D.cutouts[lb + "_left"]), loadImg(D.cutouts[rb + "_right"])]).then(function (imgs) {
        render(previewCanvas, b, imgs[0], imgs[1], lb, rb, 1000);
        previewCanvas.dataset.ready = "1";
      });
    }).catch(function (e) { console.warn("[twodog]", e); });
  }

  // export the current selection to a Blob (for the cart/email carry-through upload)
  window.SOF2_renderBlob = function () {
    return new Promise(function (res) {
      var off = document.createElement("canvas");
      Promise.all([loadImg(D.base), loadImg(D.cutouts[current.left + "_left"]),
                   loadImg(D.cutouts[current.right + "_right"])]).then(function (imgs) {
        render(off, imgs[0], imgs[1], imgs[2], current.left, current.right, 1200);
        off.toBlob(function (b) { res(b); }, "image/jpeg", 0.9);
      });
    });
  };

  // ─────────────────────────── CARRY-THROUGH ──────────────────────────────
  // Render + upload the current design, resolving to a public image URL.
  // Rejects (or the caller times out) on any failure — the form still submits.
  function renderAndUpload() {
    return window.SOF2_renderBlob().then(function (blob) {
      if (!blob) throw new Error("no blob");
      return fetch(UPLOAD_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": blob.type || "image/jpeg" },
        body: blob,
      });
    }).then(function (resp) {
      if (!resp.ok) throw new Error("upload " + resp.status);
      return resp.json();
    }).then(function (data) {
      if (!data || !data.url) throw new Error("no url");
      return data.url;
    });
  }

  // Resolve to the URL, or to null once UPLOAD_TIMEOUT_MS elapses / on error.
  // Never rejects — the caller always proceeds to submit.
  function uploadWithTimeout() {
    return new Promise(function (res) {
      var done = false;
      var finish = function (v) { if (!done) { done = true; res(v); } };
      var timer = setTimeout(function () { finish(null); }, UPLOAD_TIMEOUT_MS);
      renderAndUpload().then(function (url) {
        clearTimeout(timer); finish(url);
      }).catch(function (e) {
        console.warn("[twodog] preview upload skipped:", e && e.message);
        clearTimeout(timer); finish(null);
      });
    });
  }

  document.addEventListener("DOMContentLoaded", function () {
    previewCanvas = document.getElementById("sof2-canvas");
    var form = document.getElementById("sof2-form");
    var lSel = document.getElementById("sof2-left"), rSel = document.getElementById("sof2-right");
    if (!previewCanvas || !lSel || !rSel) return;
    function slugOf(sel) { var o = sel.options[sel.selectedIndex]; return (o && o.dataset && o.dataset.slug) || null; }
    function onChange() { current.left = slugOf(lSel); current.right = slugOf(rSel); redraw(); }
    lSel.addEventListener("change", onChange);
    rSel.addEventListener("change", onChange);
    onChange();   // initial (defaults, if any)

    // Carry-through: intercept the add-to-cart submit exactly once, upload the
    // preview, stash its URL in a hidden property, then let the form submit.
    if (form) {
      // Ensure the hidden field exists up front.
      var hidden = form.querySelector('input[name="properties[_preview_image]"]');
      if (!hidden) {
        hidden = document.createElement("input");
        hidden.type = "hidden";
        hidden.name = "properties[_preview_image]";
        form.appendChild(hidden);
      }

      var submitting = false;
      form.addEventListener("submit", function (ev) {
        if (submitting) return;              // second pass — let it through
        // Only bother if both breeds are chosen (required attr also enforces).
        if (!current.left || !current.right) return;
        ev.preventDefault();

        var btn = form.querySelector('button[type="submit"], button[name="add"]');
        var btnLabel = btn ? btn.textContent : null;
        if (btn) { btn.disabled = true; btn.textContent = "Adding…"; }

        uploadWithTimeout().then(function (url) {
          if (url) hidden.value = url;       // else leave blank — server ignores it
          submitting = true;
          if (btn) { btn.disabled = false; if (btnLabel != null) btn.textContent = btnLabel; }
          // Re-submit the real form. requestSubmit keeps the clicked button's
          // name=add semantics; fall back to submit() for older browsers.
          if (form.requestSubmit) { form.requestSubmit(btn || undefined); }
          else { form.submit(); }
        });
      });
    }
  });
})();
